import React, { useState, useEffect, useRef } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { useGetMainCategoriesQuery, useGetSecondCategoriesQuery, useGetSubCategoriesQuery, useGetSubPartCategoriesQuery } from '../../../../../api';
import { useLanguage } from '../../../../../contexts/LanguageContext';

const CascadingDropdowns = ({
    mainCategoryValue,
    secondCategoryValue,
    subCategoryValue,
    subPartCategoryValue,
    onMainCategoryChange,
    onSecondCategoryChange,
    onSubCategoryChange,
    onSubPartCategoryChange,
    resetOnNewRecord = false,
    isEditMode = false,
    colSize,
    disabled = false, // Add disabled prop
    readOnly = false, // Add readOnly prop for alternative naming
    isDisabled = false, // Add isDisabled prop for alternative naming
    // Validation props
    showValidation = false,
    mainCategoryError = false,
    secondCategoryError = false,
    mainCategoryErrorMessage = '',
    secondCategoryErrorMessage = ''
}) => {
    // Determine if dropdowns should be disabled - check all possible prop names
    const isDropdownDisabled = disabled || readOnly || isDisabled;

    // State for selected values
    const [mainCategory, setMainCategory] = useState(null);
    const [secondCategory, setSecondCategory] = useState(null);
    const [thirdCategory, setThirdCategory] = useState(null);
    const [finalCategory, setFinalCategory] = useState(null);

    // State for search text
    const [mainSearchText, setMainSearchText] = useState('');
    const [secondSearchText, setSecondSearchText] = useState('');
    const [thirdSearchText, setThirdSearchText] = useState('');
    const [finalSearchText, setFinalSearchText] = useState('');

    // Refs to track previous values to prevent unnecessary updates
    const prevMainCategoryValueRef = useRef(null);
    const prevSecondCategoryValueRef = useRef(null);
    const prevSubCategoryValueRef = useRef(null);
    const prevSubPartCategoryValueRef = useRef(null);

    // Refs to track if options are loaded
    const mainOptionsLoadedRef = useRef(false);
    const secondOptionsLoadedRef = useRef(false);
    const thirdOptionsLoadedRef = useRef(false);
    const finalOptionsLoadedRef = useRef(false);

    const { getCurrentLanguageId } = useLanguage();

    const languageId = getCurrentLanguageId();

    // Fetch main categories from API with search parameter
    const { data: mainCategoriesList = [], isLoading: isLoadingMainCategories } = useGetMainCategoriesQuery({
        PageSize: 30, // Changed from RowCount to PageSize
        SearchText: mainSearchText,
        LanguageId: languageId
    });

    // Fetch second categories from API with search parameter and mainCategory (using lookupCode)
    const { data: secondCategoriesList = [], isLoading: isLoadingSecondCategories } = useGetSecondCategoriesQuery(
        {
            PageSize: 30,
            SearchText: secondSearchText,
            mainCategory: mainCategory?.lookupCode, // Using lookupCode instead of id
            LanguageId: languageId
        },
        { skip: !mainCategory }
    );

    // Fetch sub categories from API with search parameter, mainCategory and secondaryCategory (using lookupCode)
    const { data: subCategoriesList = [], isLoading: isLoadingSubCategories } = useGetSubCategoriesQuery(
        {
            PageSize: 30,
            SearchText: thirdSearchText,
            mainCategory: mainCategory?.lookupCode, // Using lookupCode instead of id
            secondaryCategory: secondCategory?.lookupCode, // Using lookupCode instead of id
            LanguageId: languageId
        },
        { skip: !mainCategory || !secondCategory }
    );

    // Fetch sub part categories from API with search parameter, mainCategory, secondaryCategory and subCategory (using lookupCode)
    const { data: subPartCategoriesList = [], isLoading: isLoadingSubPartCategories } = useGetSubPartCategoriesQuery(
        {
            PageSize: 30,
            SearchText: finalSearchText,
            mainCategory: mainCategory?.lookupCode, // Using lookupCode instead of id
            secondaryCategory: secondCategory?.lookupCode, // Using lookupCode instead of id
            subCategory: thirdCategory?.lookupCode, // Using lookupCode instead of id
            LanguageId: languageId
        },
        { skip: !mainCategory || !secondCategory || !thirdCategory }
    );

    // Transform categories lists for dropdowns
    const mainOptions =
        mainCategoriesList.data && mainCategoriesList.data.length > 0
            ? mainCategoriesList.data.map((category) => ({
                  id: category.id,
                  text: category.mainCategoryName,
                  order: category.order,
                  lookupCode: category.lookupCode || category.id, // Add lookupCode
                  displayValue: `${category.id} - ${category.mainCategoryName}`
              }))
            : [];

    const secondOptions =
        secondCategoriesList.data && secondCategoriesList.data.length > 0
            ? secondCategoriesList.data.map((category) => ({
                  id: category.id,
                  text: category.secondaryCategoryName,
                  order: category.order,
                  lookupCode: category.lookupCode || category.id, // Add lookupCode
                  displayValue: `${category.id} - ${category.secondaryCategoryName}`
              }))
            : [];

    const thirdOptions =
        subCategoriesList.data && subCategoriesList.data.length > 0
            ? subCategoriesList.data.map((category) => ({
                  id: category.id,
                  text: category.subCategoryName,
                  order: category.order,
                  lookupCode: category.lookupCode || category.id, // Add lookupCode
                  displayValue: `${category.id} - ${category.subCategoryName}`
              }))
            : [];

    const finalOptions =
        subPartCategoriesList.data && subPartCategoriesList.data.length > 0
            ? subPartCategoriesList.data.map((category) => ({
                  id: category.id,
                  text: category.subPartCategoryName,
                  order: category.order,
                  lookupCode: category.lookupCode || category.id, // Add lookupCode
                  displayValue: `${category.id} - ${category.subPartCategoryName}`
              }))
            : [];

    // Update options loaded refs when options change
    useEffect(() => {
        if (mainOptions.length > 0) {
            mainOptionsLoadedRef.current = true;
        }
    }, [mainOptions]);

    useEffect(() => {
        if (secondOptions.length > 0) {
            secondOptionsLoadedRef.current = true;
        }
    }, [secondOptions]);

    useEffect(() => {
        if (thirdOptions.length > 0) {
            thirdOptionsLoadedRef.current = true;
        }
    }, [thirdOptions]);

    useEffect(() => {
        if (finalOptions.length > 0) {
            finalOptionsLoadedRef.current = true;
        }
    }, [finalOptions]);

    // Handle selection changes - only if not disabled
    const handleMainChange = (e) => {
        if (isDropdownDisabled) return;

        const newValue = e.value;

        // Only update if the value has changed
        if (newValue?.id !== mainCategory?.id) {
            setMainCategory(newValue);

            // Reset dependent dropdowns
            setSecondCategory(null);
            setThirdCategory(null);
            setFinalCategory(null);

            // Update parent component values
            if (onMainCategoryChange) {
                onMainCategoryChange(newValue ? newValue.displayValue : '');
            }

            // Reset dependent dropdowns in parent component
            if (onSecondCategoryChange) {
                onSecondCategoryChange('');
            }
            if (onSubCategoryChange) {
                onSubCategoryChange('');
            }
            if (onSubPartCategoryChange) {
                onSubPartCategoryChange('');
            }

            // Reset options loaded refs for dependent dropdowns
            secondOptionsLoadedRef.current = false;
            thirdOptionsLoadedRef.current = false;
            finalOptionsLoadedRef.current = false;
        }
    };

    const handleSecondChange = (e) => {
        if (isDropdownDisabled) return;

        const newValue = e.value;

        // Only update if the value has changed
        if (newValue?.id !== secondCategory?.id) {
            setSecondCategory(newValue);

            // Reset dependent dropdowns
            setThirdCategory(null);
            setFinalCategory(null);

            // Update parent component values
            if (onSecondCategoryChange) {
                onSecondCategoryChange(newValue ? newValue.displayValue : '');
            }

            // Reset dependent dropdowns in parent component
            if (onSubCategoryChange) {
                onSubCategoryChange('');
            }
            if (onSubPartCategoryChange) {
                onSubPartCategoryChange('');
            }

            // Reset options loaded refs for dependent dropdowns
            thirdOptionsLoadedRef.current = false;
            finalOptionsLoadedRef.current = false;
        }
    };

    const handleThirdChange = (e) => {
        if (isDropdownDisabled) return;

        const newValue = e.value;

        // Only update if the value has changed
        if (newValue?.id !== thirdCategory?.id) {
            setThirdCategory(newValue);

            // Reset dependent dropdown
            setFinalCategory(null);

            // Update parent component values
            if (onSubCategoryChange) {
                onSubCategoryChange(newValue ? newValue.displayValue : '');
            }

            // Reset dependent dropdown in parent component
            if (onSubPartCategoryChange) {
                onSubPartCategoryChange('');
            }

            // Reset options loaded ref for dependent dropdown
            finalOptionsLoadedRef.current = false;
        }
    };

    const handleFinalChange = (e) => {
        if (isDropdownDisabled) return;

        const newValue = e.value;

        // Only update if the value has changed
        if (newValue?.id !== finalCategory?.id) {
            setFinalCategory(newValue);

            // Update parent component value
            if (onSubPartCategoryChange) {
                onSubPartCategoryChange(newValue ? newValue.displayValue : '');
            }
        }
    };

    // Reset all dropdowns when resetOnNewRecord changes to true
    useEffect(() => {
        if (resetOnNewRecord) {

            // Reset all dropdown values
            setMainCategory(null);
            setSecondCategory(null);
            setThirdCategory(null);
            setFinalCategory(null);

            // Reset search texts
            setMainSearchText('');
            setSecondSearchText('');
            setThirdSearchText('');
            setFinalSearchText('');

            // Reset parent component values
            if (onMainCategoryChange) onMainCategoryChange('');
            if (onSecondCategoryChange) onSecondCategoryChange('');
            if (onSubCategoryChange) onSubCategoryChange('');
            if (onSubPartCategoryChange) onSubPartCategoryChange('');

            // Reset previous value refs
            prevMainCategoryValueRef.current = null;
            prevSecondCategoryValueRef.current = null;
            prevSubCategoryValueRef.current = null;
            prevSubPartCategoryValueRef.current = null;

            // Reset options loaded refs
            mainOptionsLoadedRef.current = false;
            secondOptionsLoadedRef.current = false;
            thirdOptionsLoadedRef.current = false;
            finalOptionsLoadedRef.current = false;
        }
    }, [resetOnNewRecord, onMainCategoryChange, onSecondCategoryChange, onSubCategoryChange, onSubPartCategoryChange]);

    // Set main category value when options are loaded
    useEffect(() => {
        // Skip if no value or options, or if value hasn't changed
        if (mainCategoryValue === null || mainCategoryValue === undefined || mainOptions.length === 0 || mainCategoryValue === prevMainCategoryValueRef.current) {
            return;
        }


        // Find matching option by ID
        let mainCategoryId;

        if (typeof mainCategoryValue === 'object' && mainCategoryValue !== null) {
            // If it's an object, use its id property
            mainCategoryId = mainCategoryValue.id;
        } else if (typeof mainCategoryValue === 'string') {
            // If it's a string, try to extract ID from displayValue format
            const parts = mainCategoryValue.split(' - ');
            mainCategoryId = parts[0];
        } else {
            // Otherwise use it directly (number or other primitive)
            mainCategoryId = mainCategoryValue;
        }

        const found = mainOptions.find((option) => option.id == mainCategoryId);

        if (found && (!mainCategory || found.id !== mainCategory.id)) {
            setMainCategory(found);

            // Update previous value ref
            prevMainCategoryValueRef.current = mainCategoryValue;
        }
    }, [mainCategoryValue, mainOptions, mainCategory]);

    // Set second category value when options are loaded and main category is set
    useEffect(() => {
        // Skip if no value or options, or if main category is not set, or if value hasn't changed
        if (secondCategoryValue === null || secondCategoryValue === undefined || secondOptions.length === 0 || !mainCategory || secondCategoryValue === prevSecondCategoryValueRef.current) {
            return;
        }


        // Find matching option by ID
        let secondCategoryId;

        if (typeof secondCategoryValue === 'object' && secondCategoryValue !== null) {
            // If it's an object, use its id property
            secondCategoryId = secondCategoryValue.id;
        } else if (typeof secondCategoryValue === 'string') {
            // If it's a string, try to extract ID from displayValue format
            const parts = secondCategoryValue.split(' - ');
            secondCategoryId = parts[0];
        } else {
            // Otherwise use it directly (number or other primitive)
            secondCategoryId = secondCategoryValue;
        }

        const found = secondOptions.find((option) => option.id == secondCategoryId);

        if (found && (!secondCategory || found.id !== secondCategory.id)) {
            setSecondCategory(found);

            // Update previous value ref
            prevSecondCategoryValueRef.current = secondCategoryValue;
        }
    }, [secondCategoryValue, secondOptions, secondCategory, mainCategory]);

    // Set third category value when options are loaded and second category is set
    useEffect(() => {
        // Skip if no value or options, or if second category is not set, or if value hasn't changed
        if (subCategoryValue === null || subCategoryValue === undefined || thirdOptions.length === 0 || !secondCategory || subCategoryValue === prevSubCategoryValueRef.current) {
            return;
        }


        // Find matching option by ID
        let subCategoryId;

        if (typeof subCategoryValue === 'object' && subCategoryValue !== null) {
            // If it's an object, use its id property
            subCategoryId = subCategoryValue.id;
        } else if (typeof subCategoryValue === 'string') {
            // If it's a string, try to extract ID from displayValue format
            const parts = subCategoryValue.split(' - ');
            subCategoryId = parts[0];
        } else {
            // Otherwise use it directly (number or other primitive)
            subCategoryId = subCategoryValue;
        }

        const found = thirdOptions.find((option) => option.id == subCategoryId);

        if (found && (!thirdCategory || found.id !== thirdCategory.id)) {
            setThirdCategory(found);

            // Update previous value ref
            prevSubCategoryValueRef.current = subCategoryValue;
        }
    }, [subCategoryValue, thirdOptions, thirdCategory, secondCategory]);

    // Set final category value when options are loaded and third category is set
    useEffect(() => {
        // Skip if no value or options, or if third category is not set, or if value hasn't changed
        if (subPartCategoryValue === null || subPartCategoryValue === undefined || finalOptions.length === 0 || !thirdCategory || subPartCategoryValue === prevSubPartCategoryValueRef.current) {
            return;
        }


        // Find matching option by ID
        let subPartCategoryId;

        if (typeof subPartCategoryValue === 'object' && subPartCategoryValue !== null) {
            // If it's an object, use its id property
            subPartCategoryId = subPartCategoryValue.id;
        } else if (typeof subPartCategoryValue === 'string') {
            // If it's a string, try to extract ID from displayValue format
            const parts = subPartCategoryValue.split(' - ');
            subPartCategoryId = parts[0];
        } else {
            // Otherwise use it directly (number or other primitive)
            subPartCategoryId = subPartCategoryValue;
        }

        const found = finalOptions.find((option) => option.id == subPartCategoryId);

        if (found && (!finalCategory || found.id !== finalCategory.id)) {
            setFinalCategory(found);

            // Update previous value ref
            prevSubPartCategoryValueRef.current = subPartCategoryValue;
        } 
    }, [subPartCategoryValue, finalOptions, finalCategory, thirdCategory]);

    // Handle filter changes - only if not disabled
    const handleMainFilterChange = (e) => {
        if (!isDropdownDisabled) {
            setMainSearchText(e.filter);
        }
    };

    const handleSecondFilterChange = (e) => {
        if (!isDropdownDisabled) {
            setSecondSearchText(e.filter);
        }
    };

    const handleThirdFilterChange = (e) => {
        if (!isDropdownDisabled) {
            setThirdSearchText(e.filter);
        }
    };

    const handleFinalFilterChange = (e) => {
        if (!isDropdownDisabled) {
            setFinalSearchText(e.filter);
        }
    };

    const { th } = useLanguage();

    // CSS stilleri için style objesi
    const dropdownContainerStyle = {
        maxWidth: '100%',
        overflow: 'hidden'
    };

    const dropdownStyle = {
        maxWidth: '100%',
        width: '100%'
    };

    const itemTemplateStyle = {
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    };

    return (
        <>
            <div className={`col-12 ${colSize ? '' : 'md:col-6'} `} style={{ flex: colSize, ...dropdownContainerStyle }}>
                <label className="block font-bold mb-2">{th('preApproval.form.mainCategoryLabel')+' *'}</label>
                <Dropdown
                    className={showValidation && mainCategoryError ? 'p-invalid w-full' : 'w-full'}
                    style={dropdownStyle}
                    value={mainCategory}
                    scrollHeight="400px"
                    options={mainOptions}
                    onChange={handleMainChange}
                    onFilter={handleMainFilterChange}
                    optionLabel="displayValue"
                    filter={!isDropdownDisabled}
                    showClear={!isDropdownDisabled}
                    filterBy="id,text"
                    placeholder={th('preApproval.form.placeholders.mainCategory')}
                    loading={isLoadingMainCategories}
                    disabled={isDropdownDisabled}
                    itemTemplate={(option) => (
                        <div style={itemTemplateStyle} title={`${option.id} - ${option.text}`}>
                            <span style={{ fontWeight: 'bold' }}>{option.id}</span> - {option.text}
                        </div>
                    )}
                />
                {showValidation && mainCategoryError && <small className="p-error">{mainCategoryErrorMessage}</small>}
            </div>
            <div className={`col-12 ${colSize ? '' : 'md:col-6'} `} style={{ flex: colSize, ...dropdownContainerStyle }}>
                <label className="block font-bold mb-2">{th('preApproval.form.secondCategoryLabel')+' *'}</label>
                <Dropdown
                    className={showValidation && secondCategoryError ? 'p-invalid w-full' : 'w-full'}
                    style={dropdownStyle}
                    value={secondCategory}
                    scrollHeight="400px"
                    options={secondOptions}
                    onChange={handleSecondChange}
                    onFilter={handleSecondFilterChange}
                    optionLabel="displayValue"
                    filter={!isDropdownDisabled}
                    showClear={!isDropdownDisabled}
                    filterBy="id,text"
                    placeholder={th('preApproval.form.placeholders.secondCategory')}
                    disabled={isDropdownDisabled || !mainCategory}
                    loading={isLoadingSecondCategories}
                    itemTemplate={(option) => (
                        <div style={itemTemplateStyle} title={`${option.id} - ${option.text}`}>
                            <span style={{ fontWeight: 'bold' }}>{option.id}</span> - {option.text}
                        </div>
                    )}
                />
                {showValidation && secondCategoryError && <small className="p-error">{secondCategoryErrorMessage}</small>}
            </div>
            <div className={`col-12 ${colSize ? '' : 'md:col-6'} `} style={{ flex: colSize, ...dropdownContainerStyle }}>
                <label className="block font-bold mb-2">{th('preApproval.form.subCategoryLabel')}</label>
                <Dropdown
                    className="w-full"
                    style={dropdownStyle}
                    scrollHeight="400px"
                    value={thirdCategory}
                    options={thirdOptions}
                    onChange={handleThirdChange}
                    onFilter={handleThirdFilterChange}
                    optionLabel="displayValue"
                    filter={!isDropdownDisabled}
                    showClear={!isDropdownDisabled}
                    filterBy="id,text"
                    placeholder={th('preApproval.form.placeholders.subCategory')}
                    disabled={isDropdownDisabled || !secondCategory}
                    loading={isLoadingSubCategories}
                    itemTemplate={(option) => (
                        <div style={itemTemplateStyle} title={`${option.id} - ${option.text}`}>
                            <span style={{ fontWeight: 'bold' }}>{option.id}</span> - {option.text}
                        </div>
                    )}
                />
            </div>
            <div className={`col-12 ${colSize ? '' : 'md:col-6'} `} style={{ flex: colSize, ...dropdownContainerStyle }}>
                <label className="block font-bold mb-2">{th('preApproval.form.subPartCategoryLabel')}</label>
                <Dropdown
                    className="w-full"
                    style={dropdownStyle}
                    scrollHeight="400px"
                    value={finalCategory}
                    options={finalOptions}
                    onChange={handleFinalChange}
                    onFilter={handleFinalFilterChange}
                    optionLabel="displayValue"
                    filter={!isDropdownDisabled}
                    showClear={!isDropdownDisabled}
                    filterBy="id,text"
                    placeholder={th('preApproval.form.placeholders.subPartCategory')}
                    disabled={isDropdownDisabled || !thirdCategory}
                    loading={isLoadingSubPartCategories}
                    itemTemplate={(option) => (
                        <div style={itemTemplateStyle} title={`${option.id} - ${option.text}`}>
                            <span style={{ fontWeight: 'bold' }}>{option.id}</span> - {option.text}
                        </div>
                    )}
                />
            </div>
        </>
    );
};

export default CascadingDropdowns; 
